import { ColumnDef } from "@tanstack/react-table";
import { Link } from "@heroui/link";
import type { TFunction } from "i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { getAuditColumns } from "@/pages/a7/Audit/getAuditColumns";

import { formatNumber } from "@/lib/utils";

import { CheckIcon } from "@radix-ui/react-icons";

export type A7FIAIssuerTS = {
  id: number;
  date: Date;
  a7_fia_strategy_id: number;
  a7_fia_underlying_id: number;
  a7_fia_time_serie_file_id: number;
  type_de_contrepartie: string;
  secteur_nace: string;
  code_iso_du_pays: string;
  valorisation_part_du_sous_jacent: number;
  detention_du_fonds_dans_le_sous_jacent: number;
  version: number;
  a7_fia_issuer_id?: number;
};

const tableName = "a7_fia_issuer_ts";

export const getIssuerTsColumns = (
  t: TFunction,
): ColumnDef<A7FIAIssuerTS>[] => {
  const tTable = (key: string, defaultValue: string) =>
    t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });
  const tCommon = (key: string, defaultValue: string) =>
    t(`actions.${key}`, { ns: "common", defaultValue });

  return [
    {
      accessorKey: "id",
      header: tTable("id", "ID"),
    },
    {
      accessorKey: "date",
      header: tTable("date", "Date"),
    },
    {
      accessorKey: "a7_fia_issuer_name",
      header: tTable("a7_fia_issuer_name", "Issuer"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-fia/a7_fia_issuers/${row.original.a7_fia_issuer_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_fia_issuer_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "type_de_contrepartie",
      header: tTable("type_de_contrepartie", "Counterparty type"),
    },
    {
      accessorKey: "secteur_nace",
      header: tTable("secteur_nace", "Sector"),
    },
    {
      accessorKey: "code_iso_du_pays",
      header: tTable("code_iso_du_pays", "Country ISO code"),
    },
    {
      accessorKey: "chiffre_d_affaires_du_sous_jacent",
      header: () => (
        <div className="text-right">
          {tTable("chiffre_d_affaires_du_sous_jacent", "Revenue")}
        </div>
      ),
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          value != null && (
            <div className="text-right">{formatNumber(value as number)}</div>
          )
        );
      },
    },
    {
      accessorKey: "nb_employes_contrepartie",
      header: () => (
        <div className="text-right">
          {tTable("nb_employes_contrepartie", "Employees")}
        </div>
      ),
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          value != null && (
            <div className="text-right">{formatNumber(value as number)}</div>
          )
        );
      },
    },
    {
      accessorKey: "actionnaire_majoritaire_public",
      header: () => (
        <div className="text-center">
          {tTable("actionnaire_majoritaire_public", "Majority shareholder")}
        </div>
      ),
      cell: ({ getValue }) =>
        getValue() == "YES" && (
          <div className="flex justify-center">
            <CheckIcon />
          </div>
        ),
    },
    {
      accessorKey: "notation_moodys_contrepartie",
      header: tTable("notation_moodys_contrepartie", "Moody's rating"),
    },
    {
      accessorKey: "notation_fitch_contrepartie",
      header: tTable("notation_fitch_contrepartie", "Fitch rating"),
    },
    {
      accessorKey: "notation_sp_contrepartie",
      header: tTable("notation_sp_contrepartie", "S&P rating"),
    },
    {
      accessorKey: "cqs_contrepartie",
      header: tTable("cqs_contrepartie", "CQS"),
    },
    {
      accessorKey: "sous_jacent_en_defaut",
      header: () => (
        <div className="text-center">
          {tTable("sous_jacent_en_defaut", "Default")}
        </div>
      ),
      cell: ({ getValue }) =>
        getValue() == "YES" && (
          <div className="flex justify-center">
            <CheckIcon />
          </div>
        ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        return (
          <div className="flex justify-end">
            <Link
              href={`${window.location.pathname}/${row.getValue("id")}`}
              className="text-xs"
              underline="hover"
            >
              {tCommon("edit", "Edit")}
            </Link>
          </div>
        );
      },
    },
  ];
};

export const getIssuerTsAuditColumns = (t: TFunction) =>
  getAuditColumns<A7FIAIssuerTS>(getIssuerTsColumns(t)).filter((column) => {
    const accessorKey =
      "accessorKey" in column
        ? String((column as { accessorKey?: unknown }).accessorKey ?? "")
        : "";
    return !["a7_fia_issuer_name", "id"].includes(accessorKey);
  });
